import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'lib/i18n';
import { useRouter } from 'next/router';
import PricingBlock from '../PricingBlock';
import { Button } from 'client/components/shared';
import CheckedIcon from 'client/components/settings/billing/new-subscription/CheckedIcon.svg';
import { useGetPrice } from 'client/components/settings/billing/useGetPrices';
import { getCurrentUser } from 'components/users/WithCurrentUser/WithCurrentUser';

const Container = styled.div`
  max-width: 280px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div``;

const Price = styled.h1`
  font-size: 7rem;
  margin-bottom: 10px;
`;

const Interval = styled.span`
  font-size: 2.4rem;
  font-weight: 400;
`;

const Title = styled.h2`
  font-weight: 400;
  margin: 0 0 12px;
`;

const Features = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 10px;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  padding: 3px 0px;
  white-space: nowrap;
`;

const StyledCheckedIcon = styled(CheckedIcon)`
  margin-right: 7px;
`;

const Note = styled.div`
  color: ${p => p.theme.currentTheme.notes};
  padding: 0 5px;
  margin-top: 20px;
`;

const SelectButton = styled(Button as any)`
  font-size: 2rem;
  line-height: 2.4rem;
  letter-spacing: -0.08rem;
  padding: 0 23px;
  height: 43px;
  border-radius: 38px;
  margin-top: 20px;
  margin-bottom: 20px;

  a {
    color: ${p => p.theme.colors.white};
  }
`;

const PlansContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 80px;
  flex-wrap: wrap;
`;

const CenteredTitle = styled.h2`
  text-align: center;
  margin-top: 100px;
  font-weight: 400;
`;

interface PlanSelectButtonProps {
  onClick: () => void;
}

const PlanSelectButton = ({ onClick }: PlanSelectButtonProps) => {
  const { t } = useTranslation('common');

  return (
    <SelectButton primary={true} onClick={onClick}>
      {t('subscriptions.selectPlan')}
    </SelectButton>
  );
};

interface PricingPlansProps {
  onFreePlanClick: () => void;
  onProPlanClick: () => void;
}

const PricingPlans = ({ onFreePlanClick, onProPlanClick }: PricingPlansProps) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const proFeatures = t('subscriptions.plans.professional.features', {
    returnObjects: true,
  }) as string[];
  const freeFeatures = t('subscriptions.plans.free.features', { returnObjects: true }) as string[];
  const prices = useGetPrice();
  const yearPrice = prices.year;
  const monthPrice = prices.month;

  const priceMonth = monthPrice ? monthPrice.amountCents / 100 : 0;
  const yearlyPerMonth = yearPrice ? yearPrice.amountCents / 100 / 12 : 0;
  const currentUser = getCurrentUser();

  const handleFreePlanClick = () => {
    if (currentUser) {
      router.push('/');
    } else {
      router.push('/signin');
    }
  };

  const handleProPlanClick = () => {
    if (currentUser) {
      router.push('/');
    } else {
      router.push('/signin');
    }
  };

  return (
    <PricingBlock>
      <PlansContainer>
        <Container>
          <Wrapper>
            <Price>
              Free
              <Interval></Interval>
            </Price>
            <Title>{t(`subscriptions.plans.free.title`)}</Title>
            <PlanSelectButton onClick={onFreePlanClick} />
            <Features>
              {freeFeatures.map(feature => (
                <Feature key={feature}>
                  <StyledCheckedIcon />
                  {feature}
                </Feature>
              ))}
            </Features>
          </Wrapper>
        </Container>

        <Container>
          <Wrapper>
            <Price>
              ${priceMonth}
              <Interval>user/{t(`subscriptions.interval.month.shortTitle`)}</Interval>
            </Price>
            <Title>{t(`subscriptions.plans.professional.title`)}</Title>
            <PlanSelectButton onClick={onProPlanClick} />
            <Features>
              {proFeatures.map(feature => (
                <Feature key={feature}>
                  <StyledCheckedIcon />
                  {feature}
                </Feature>
              ))}
            </Features>
            <Note>
              {t('subscriptions.plans.professional.yearlyNote', {
                perSeat: yearlyPerMonth,
                defaultValue: `$${yearlyPerMonth}/user/mo if billed annually.`,
              })}
            </Note>
          </Wrapper>
        </Container>
      </PlansContainer>
      <CenteredTitle>{t('subscriptions.chooseWisely')}</CenteredTitle>
    </PricingBlock>
  );
};

export default PricingPlans;
